import styled, { createGlobalStyle, keyframes } from 'styled-components'

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`

const c = {
  bg: '#0a0c0f', bg2: '#111318', bg3: '#181c23', bg4: '#1e2330',
  bdr: '#1f2430', bdr2: '#2a3040',
  acc: '#00e5a0', acc2: '#00b87a',
  adim: 'rgba(0,229,160,.08)', adim2: 'rgba(0,229,160,.18)',
  txt: '#e8ecf0', txt2: '#8a95a3', txt3: '#5a6370',
  warn: '#f59e0b', danger: '#ef4444', info: '#3b82f6', purple: '#a855f7',
  mono: "'Space Mono',monospace", sans: "'DM Sans',sans-serif",
}

// Paleta de "chips" por módulo — cada módulo tem uma cor visual distinta
export const MODULE_COLORS = {
  recepcao:      { bg: 'rgba(59,130,246,.15)',  color: '#93c5fd' },
  conferencia:   { bg: 'rgba(0,229,160,.15)',   color: '#00e5a0' },
  separacao:     { bg: 'rgba(245,158,11,.15)',  color: '#fbbf24' },
  cadastro:      { bg: 'rgba(236,72,153,.15)',  color: '#f9a8d4' },
  embarque:      { bg: 'rgba(168,85,247,.15)',  color: '#c084fc' },
  devolucao:     { bg: 'rgba(239,68,68,.15)',   color: '#f87171' },
  transferencia: { bg: 'rgba(6,182,212,.15)',   color: '#67e8f9' },
  romaneio:      { bg: 'rgba(139,92,246,.15)',  color: '#a5b4fc' },
  admin:         { bg: 'rgba(148,163,184,.15)', color: '#cbd5e1' },
  webhook:       { bg: 'rgba(107,114,128,.15)', color: '#9ca3af' },
}

export const GlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
`

export const PageWrap = styled.div`
  background:${c.bg}; color:${c.txt}; font-family:${c.sans};
  height:100vh; display:flex; flex-direction:column; overflow:hidden;
`

export const Topbar = styled.div`
  background:${c.bg2}; border-bottom:1px solid ${c.bdr};
  padding:10px 16px; height:48px;
  display:flex; align-items:center; gap:14px; flex-shrink:0;
  h2 { font-size:13px; font-weight:600; color:${c.txt}; }
  .crumb { font-size:11px; color:${c.txt3}; }
`

export const Body = styled.div`flex:1; display:flex; flex-direction:column; min-height:0;`

// ─── FILTROS ─────────────────────────────────────────────────────────────
export const FilterBar = styled.div`
  background:${c.bg2}; border-bottom:1px solid ${c.bdr};
  padding:14px 20px; display:grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap:10px; flex-shrink:0;
`
export const FilterGroup = styled.div`display:flex; flex-direction:column; gap:4px;`
export const FilterLabel = styled.label`
  font-size:10px; color:${c.txt3}; text-transform:uppercase;
  letter-spacing:.06em; font-weight:700;
`
export const FilterInput = styled.input`
  background:${c.bg3}; border:1px solid ${c.bdr2}; border-radius:7px;
  color:${c.txt}; padding:8px 10px; font-size:12px; font-family:${c.sans}; outline:none;
  &:focus { border-color:${c.acc}; }
  &::placeholder { color:${c.txt3}; }
`
export const FilterSelect = styled.select`
  background:${c.bg3}; border:1px solid ${c.bdr2}; border-radius:7px;
  color:${c.txt}; padding:8px 10px; font-size:12px; font-family:${c.sans}; outline:none;
  cursor:pointer;
  &:focus { border-color:${c.acc}; }
  option { background:${c.bg3}; color:${c.txt}; }
`

export const FilterActions = styled.div`
  display:flex; gap:8px; align-items:end; padding:0 20px 14px;
  background:${c.bg2}; border-bottom:1px solid ${c.bdr};
  button {
    background:transparent; border:1px solid ${c.bdr2}; color:${c.txt2};
    padding:6px 12px; border-radius:6px; font-size:11px; cursor:pointer;
    font-family:${c.sans}; transition:.12s;
    &:hover:not(:disabled) { border-color:${c.acc}; color:${c.acc}; }
    &:disabled { opacity:.4; cursor:not-allowed; }
  }
  .primary {
    background:${c.acc}; color:#000; border:none; font-weight:700;
    &:hover { filter:brightness(1.1); }
  }
  .spacer { flex:1; }
  .count {
    font-size:11px; color:${c.txt3};
    strong { color:${c.acc}; font-family:${c.mono}; font-size:13px; margin-right:3px; }
  }
`

// ─── TABELA ──────────────────────────────────────────────────────────────
export const TableWrap = styled.div`
  flex:1; overflow:auto; background:${c.bg};
  &::-webkit-scrollbar { width:6px; height:6px; }
  &::-webkit-scrollbar-thumb { background:${c.bdr2}; border-radius:3px; }
`
export const Table = styled.div`
  display:grid;
  grid-template-columns: 150px 130px 120px 120px 180px 120px minmax(240px,1.7fr) minmax(150px,1fr) 40px;
  min-width: 100%;
`
export const TableHead = styled.div`
  display: contents;
  > span {
    background:${c.bg2}; padding:10px 12px; font-size:10px; font-weight:700;
    color:${c.txt2}; text-transform:uppercase; letter-spacing:.06em;
    border-bottom:1px solid ${c.bdr2}; position:sticky; top:0; z-index:2;
  }
`
export const TableRow = styled.div`
  display: contents;
  &:hover > span { background:${c.bg3}; }
  > span {
    padding:10px 12px; font-size:12px; color:${c.txt};
    border-bottom:1px solid ${c.bdr}; display:flex; align-items:center;
    background:${c.bg2};
    transition: background .1s;
    &.mono { font-family:${c.mono}; font-size:11px; }
    &.dim { color:${c.txt3}; font-size:11px; }
    &.actionCell { color:${c.acc}; font-family:${c.mono}; font-size:11px; font-weight:700; }
    &.entityCell { color:${c.txt}; font-weight:600; }
    &.desc { white-space:normal; line-height:1.35; color:${c.txt}; }
    &.transitionCell {
      font-family:${c.mono}; font-size:11px;
      .arrow { color:${c.txt3}; margin:0 6px; }
      .from { color:${c.txt3}; }
      .to { color:${c.acc}; font-weight:700; }
    }
    &.rowBtn { justify-content:center; }
  }
`
export const RowBtn = styled.button`
  background:transparent; border:1px solid ${c.bdr2}; color:${c.txt2};
  width:26px; height:26px; border-radius:6px; cursor:pointer; font-size:14px;
  display:flex; align-items:center; justify-content:center;
  &:hover { border-color:${c.acc}; color:${c.acc}; }
`

// ─── CARTÕES DE ALERTA (divergências) ────────────────────────────────────
export const AlertList = styled.div`
  flex:1; overflow:auto; padding:16px 20px;
  display:flex; flex-direction:column; gap:12px;
  &::-webkit-scrollbar { width:6px; }
  &::-webkit-scrollbar-thumb { background:${c.bdr2}; border-radius:3px; }
`
export const AlertCard = styled.div`
  background:${c.bg2};
  border:1px solid rgba(239,68,68,.35);
  border-left:4px solid ${c.danger};
  border-radius:10px;
  padding:14px 16px;
  cursor:pointer;
  animation:${fadeIn} .15s ease;
  transition:border-color .12s, background .12s;
  &:hover { background:${c.bg3}; border-color:rgba(239,68,68,.6); }

  .ahead {
    display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-bottom:8px;
    .tag { font-size:10px; font-weight:700; letter-spacing:.06em; text-transform:uppercase; color:${c.danger}; }
    .time { font-family:${c.mono}; font-size:11px; color:${c.txt2}; }
    .user { font-size:12px; font-weight:600; color:${c.txt}; }
    .action { font-family:${c.mono}; font-size:11px; color:${c.txt3}; margin-left:auto; }
  }
  .msg { font-size:13.5px; color:${c.txt}; line-height:1.5; }
  .reason {
    display:inline-block; margin-bottom:6px; font-family:${c.mono}; font-size:10px;
    font-weight:700; color:#fca5a5; text-transform:uppercase; letter-spacing:.05em;
  }
  .cmp { display:flex; flex-direction:column; gap:4px; margin-top:10px; }
  .cmp .row { display:flex; gap:8px; align-items:baseline; }
  .cmp .lbl {
    flex:0 0 auto; min-width:64px; font-size:10px; font-weight:700;
    text-transform:uppercase; letter-spacing:.05em;
  }
  .cmp .scanned .lbl { color:${c.warn}; }
  .cmp .correct .lbl { color:${c.acc}; }
  .cmp .val { color:${c.txt}; font-family:${c.mono}; font-size:12px; word-break:break-word; }
`

export const Chip = styled.span`
  display:inline-flex; align-items:center; padding:2px 9px; border-radius:11px;
  font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.04em;
  background:${({ $bg }) => $bg || 'rgba(148,163,184,.15)'};
  color:${({ $color }) => $color || '#cbd5e1'};
  border:1px solid ${({ $color }) => `${$color}33` || '#33333333'};
  font-family:${c.sans};
`

export const Empty = styled.div`
  padding:60px 20px; text-align:center; color:${c.txt3}; font-size:13px; font-style:italic;
`
export const Loading = styled(Empty)``

// ─── PAGINAÇÃO ───────────────────────────────────────────────────────────
export const Pagination = styled.div`
  display:flex; align-items:center; gap:12px; padding:12px 20px;
  background:${c.bg2}; border-top:1px solid ${c.bdr}; flex-shrink:0;
  font-size:12px; color:${c.txt2};

  button {
    background:${c.bg3}; border:1px solid ${c.bdr2}; color:${c.txt};
    padding:6px 12px; border-radius:6px; cursor:pointer;
    font-size:11px; font-weight:600; font-family:${c.sans};
    &:hover:not(:disabled) { border-color:${c.acc}; color:${c.acc}; }
    &:disabled { opacity:.4; cursor:not-allowed; }
  }
  .page { color:${c.txt}; font-weight:700; }
  .spacer { flex:1; }
  select {
    background:${c.bg3}; border:1px solid ${c.bdr2}; color:${c.txt};
    padding:5px 8px; border-radius:6px; font-size:11px; cursor:pointer;
    &:focus { outline:none; border-color:${c.acc}; }
  }
`

// ─── MODAL DE DETALHE ────────────────────────────────────────────────────
export const ModalOverlay = styled.div`
  position:fixed; inset:0; background:rgba(0,0,0,.7); backdrop-filter:blur(4px);
  display:${({ $visible }) => $visible ? 'flex' : 'none'};
  align-items:center; justify-content:center; z-index:2000; padding:20px;
  animation:${fadeIn} .18s ease-out;
`
export const ModalBox = styled.div`
  background:${c.bg2}; border:1px solid ${c.bdr2}; border-radius:14px;
  width:100%; max-width:760px; max-height:88vh; overflow:hidden;
  display:flex; flex-direction:column;
`
export const ModalHead = styled.div`
  display:flex; align-items:center; justify-content:space-between;
  padding:14px 20px; border-bottom:1px solid ${c.bdr};
  h3 { font-size:14px; font-weight:700; color:${c.txt}; }
  .close {
    background:transparent; border:none; color:${c.txt2}; font-size:18px;
    cursor:pointer; padding:4px 10px; border-radius:6px;
    &:hover { background:${c.bg3}; color:${c.txt}; }
  }
`
export const ModalBody = styled.div`
  flex:1; overflow-y:auto; padding:18px 20px;
  &::-webkit-scrollbar { width:6px; }
  &::-webkit-scrollbar-thumb { background:${c.bdr2}; border-radius:3px; }
`
export const DetailGrid = styled.div`
  display:grid; grid-template-columns: 140px 1fr; gap:8px 14px;
  font-size:12px;
  .k { color:${c.txt3}; font-weight:600; text-transform:uppercase; font-size:10px; letter-spacing:.06em; }
  .v { color:${c.txt}; word-break:break-word; }
  .v.mono { font-family:${c.mono}; font-size:11px; }
`
export const PayloadBox = styled.pre`
  margin-top:14px; background:${c.bg}; border:1px solid ${c.bdr}; border-radius:8px;
  padding:12px 14px; color:${c.txt}; font-family:${c.mono}; font-size:11px;
  overflow-x:auto; white-space:pre; max-height:300px; overflow-y:auto;
  &::-webkit-scrollbar { width:6px; height:6px; }
  &::-webkit-scrollbar-thumb { background:${c.bdr2}; border-radius:3px; }
`
